/**
 * Ported from the-answer-2 (BACKLOG §16: lift the components, not the app).
 * Behaviour is theirs; the edges are savvy's. Changes made on the way in:
 *   - the field's identity is a stable string `id`, not a client-assigned
 *     `callTypeFormFieldId` number sitting beside an unrelated `id`;
 *   - a section is a flat `group` name, not a nested object with its own id;
 *   - drafts, feature announcements, router state and toasts are dropped —
 *     they belong to the host app, not the component.
 */


import React, { memo, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ArrowLeftRight, ArrowUpRightFromSquare, InfoIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { FormFieldNote } from "./form-field-note";
import type { FieldValue, FormField } from "./types";
import { Button } from "@/components/ui/button";

interface FormFieldProps {
  field: FormField;
  value: FieldValue;
  onChange: (key: string, label: string, value: FieldValue) => void;
  onUpdateAddressFields?: (
    updatedFields: { type: FormField["type"]; value: string }[],
    sourceGroup: string | undefined,
  ) => void;
  disabled?: boolean;
  groupedWith?: FormField & { value?: FieldValue };
}

export const FormFieldViewer = memo(function FormField({
  field,
  value,
  onChange,
  onUpdateAddressFields,
  disabled = false,
  groupedWith,
}: FormFieldProps) {
  const handleChange = useCallback(
    (newValue: FieldValue) => {
      onChange(field.key, field.label, newValue);
    },
    [onChange, field.key, field.label],
  );
  const handleExtensionChange = useCallback(
    (newValue: FieldValue) => {
      if (groupedWith) {
        onChange(groupedWith.key, groupedWith.label, newValue);
      }
    },
    [onChange, groupedWith],
  );

  // Handle phone + extension grouping
  if (field.type === "phone" && groupedWith?.type === "extension") {
    return (
      <PhoneWithExtensionField
        field={field}
        value={value}
        onChange={handleChange}
        onExtensionChange={handleExtensionChange}
        disabled={disabled}
        groupedWith={groupedWith}
      />
    );
  }

  // Skip rendering extension fields (they're handled by phone fields)
  if (field.type === "extension") {
    return null;
  }

  const commonProps = {
    field,
    value,
    onChange: handleChange,
    disabled,
  };

  switch (field.type) {
    case "text":
      return <TextField {...commonProps} />;
    case "phone":
      return <PhoneField {...commonProps} />;
    case "email":
      return <EmailField {...commonProps} />;
    case "textarea":
      return <TextAreaField {...commonProps} />;
    case "checkbox":
      return <CheckboxField {...commonProps} />;
    case "radio-group":
      return <RadioGroupField {...commonProps} />;
    case "date":
      return <DateField {...commonProps} />;
    case "time":
      return <TimeField {...commonProps} />;
    case "date-time":
      return <DateTimeField {...commonProps} />;
    case "note":
      return <NoteField {...commonProps} />;
    case "address-line-1":
      return (
        <AddressLine1Field
          {...commonProps}
          onUpdateAddressFields={onUpdateAddressFields}
        />
      );
    case "link":
      return (
        <div className="space-y-2">
          {field.url ? (
            <a
              href={field.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary underline"
              title={field.url}
            >
              {field.label}
              <ArrowUpRightFromSquare className="ml-1 inline-block h-4 w-4" />
            </a>
          ) : (
            <p className="text-muted-foreground text-sm">No link provided.</p>
          )}
          {field.description && (
            <p className="text-muted-foreground text-sm">{field.description}</p>
          )}
          {field.note && <FormFieldNote note={field.note} />}
        </div>
      );

    default:
      return <TextField {...commonProps} />;
  }
});

// Individual field components
interface BaseFieldProps {
  field: FormField;
  value: FieldValue;
  onChange: (value: FieldValue) => void;
  disabled: boolean;
}

interface AddressLine1FieldProps extends BaseFieldProps {
  /**
   * Fans a looked-up address across its sibling fields. Unused until savvy
   * picks an address provider — the upstream version drove it from Google
   * Places, which needs an API key this platform does not ask installs for.
   */
  onUpdateAddressFields?: (
    updatedFields: { type: FormField["type"]; value: string }[],
    sourceGroup: string | undefined,
  ) => void;
}

function AddressLine1Field({
  field,
  value,
  onChange,
  disabled,
}: AddressLine1FieldProps) {
  const [isDeclined, setIsDeclined] = React.useState(false);
  return (
    <FieldWrapper field={field}>
      <Input
        id={field.id}
        type="text"
        placeholder={field.placeholder}
        disabled={disabled || isDeclined}
        required={field.required && !isDeclined}
        value={typeof value === "string" ? value : ""}
        onChange={(e) => onChange(e.target.value)}
        className="user-invalid:border-destructive"
      />
      {field.required && (
        <DeclineCheckbox
          field={field}
          isDeclined={isDeclined}
          onDeclineChange={(declined) => {
            setIsDeclined(declined);
            onChange(declined ? null : "");
          }}
        />
      )}
    </FieldWrapper>
  );
}

function TextField({ field, value, onChange, disabled }: BaseFieldProps) {
  const [isDeclined, setIsDeclined] = React.useState(false);

  return (
    <FieldWrapper field={field}>
      <Input
        id={field.id}
        type="text"
        placeholder={field.placeholder}
        disabled={disabled || isDeclined}
        required={field.required && !isDeclined}
        value={typeof value === "string" ? value : ""}
        onChange={(e) => onChange(e.target.value)}
        className="user-invalid:border-destructive"
      />
      {field.required && (
        <DeclineCheckbox
          field={field}
          isDeclined={isDeclined}
          onDeclineChange={(declined) => {
            setIsDeclined(declined);
            onChange(declined ? null : "");
          }}
        />
      )}
    </FieldWrapper>
  );
}

function EmailField({ field, value, onChange, disabled }: BaseFieldProps) {
  const [isDeclined, setIsDeclined] = React.useState(false);
  return (
    <FieldWrapper field={field}>
      <Input
        id={field.id}
        type="email"
        placeholder={field.placeholder}
        disabled={disabled || isDeclined}
        required={field.required && !isDeclined}
        value={typeof value === "string" ? value : ""}
        onChange={(e) => onChange(e.target.value)}
        className="user-invalid:border-destructive"
      />
      {field.required && (
        <DeclineCheckbox
          field={field}
          isDeclined={isDeclined}
          onDeclineChange={(declined) => {
            setIsDeclined(declined);
            onChange(declined ? null : "");
          }}
        />
      )}
    </FieldWrapper>
  );
}

function TextAreaField({ field, value, onChange, disabled }: BaseFieldProps) {
  const [isDeclined, setIsDeclined] = React.useState(false);

  return (
    <FieldWrapper field={field}>
      <Textarea
        id={field.id}
        placeholder={field.placeholder}
        disabled={disabled || isDeclined}
        required={field.required && !isDeclined}
        value={typeof value === "string" ? value : ""}
        onChange={(e) => onChange(e.target.value)}
        className="user-invalid:border-destructive"
      />
      {field.required && field.key !== "message_text" && (
        <DeclineCheckbox
          field={field}
          isDeclined={isDeclined}
          onDeclineChange={(declined) => {
            setIsDeclined(declined);
            onChange(declined ? null : "");
          }}
        />
      )}
    </FieldWrapper>
  );
}

function PhoneField({ field, value, onChange, disabled }: BaseFieldProps) {
  const [isDeclined, setIsDeclined] = React.useState(false);

  return (
    <FieldWrapper field={field}>
      <Input
        id={field.id}
        type="tel"
        placeholder={field.placeholder}
        disabled={disabled || isDeclined}
        required={field.required && !isDeclined}
        value={typeof value === "string" ? value : ""}
        onChange={(e) => onChange(e.target.value)}
        className="user-invalid:border-destructive"
      />
      {field.required && (
        <DeclineCheckbox
          field={field}
          isDeclined={isDeclined}
          onDeclineChange={(declined) => {
            setIsDeclined(declined);
            onChange(declined ? null : "");
          }}
        />
      )}
    </FieldWrapper>
  );
}

function PhoneWithExtensionField({
  field,
  value,
  onChange,
  onExtensionChange,
  disabled,
  groupedWith,
}: {
  field: FormField;
  value: FieldValue;
  onChange: (value: FieldValue) => void;
  onExtensionChange: (value: FieldValue) => void;
  disabled: boolean;
  groupedWith: FormField & { value?: FieldValue };
}) {
  const [isDeclined, setIsDeclined] = React.useState(false);

  return (
    <FieldWrapper field={field}>
      <div className="flex gap-2">
        <div className="flex-1">
          <Input
            id={field.id}
            type="tel"
            placeholder={field.placeholder}
            disabled={disabled || isDeclined}
            required={field.required && !isDeclined}
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)}
            className="user-invalid:border-destructive"
          />
        </div>
        <div className="w-24">
          <Input
            name={groupedWith.label}
            id={groupedWith.id}
            type="text"
            placeholder={groupedWith.placeholder || "Ext"}
            disabled={disabled}
            required={groupedWith.required}
            value={
              typeof groupedWith.value === "string" ? groupedWith.value : ""
            }
            onChange={(e) => {
              onExtensionChange(e.target.value);
            }}
          />
        </div>
      </div>
      {field.required && (
        <DeclineCheckbox
          field={field}
          isDeclined={isDeclined}
          onDeclineChange={(declined) => {
            setIsDeclined(declined);
            onChange(declined ? null : "");
          }}
        />
      )}
    </FieldWrapper>
  );
}

function CheckboxField({ field, value, onChange, disabled }: BaseFieldProps) {
  return (
    <div className={cn({ "space-y-2": field.note })}>
      <div className="flex items-center space-x-2">
        <Checkbox
          id={field.id}
          disabled={disabled}
          required={field.required}
          checked={value === "yes"}
          onCheckedChange={(checked) => onChange(checked ? "yes" : "no")}
        />
        <Label htmlFor={field.id}>
          {field.label}
          {field.required && <span className="text-destructive ml-1">*</span>}
        </Label>
      </div>
      {field.description && (
        <p className="text-muted-foreground text-sm">{field.description}</p>
      )}
      {field.note && <FormFieldNote note={field.note} />}
    </div>
  );
}

function RadioGroupField({ field, value, onChange, disabled }: BaseFieldProps) {
  return (
    <FieldWrapper field={field}>
      <RadioGroup
        required={field.required}
        disabled={disabled}
        value={value}
        onValueChange={onChange}
      >
        {field.options?.map((option) => (
          <div key={option} className="flex items-center space-x-2">
            <RadioGroupItem value={option} id={`${field.id}-${option}`} />
            <Label htmlFor={`${field.id}-${option}`}>{option}</Label>
          </div>
        ))}
      </RadioGroup>
    </FieldWrapper>
  );
}

function DateField({ field, value, onChange, disabled }: BaseFieldProps) {
  const [isDeclined, setIsDeclined] = React.useState(false);

  // Determine if value is a valid date for <input type="date">
  const isValidDateValue = (val: FieldValue) => {
    if (typeof val !== "string") return false;
    // Accepts YYYY-MM-DD format only
    return /^\d{4}-\d{2}-\d{2}$/.test(val);
  };

  const initialTextMode = value && !isValidDateValue(value);
  const [isTextMode, setIsTextMode] = React.useState(initialTextMode);

  // React.useEffect(() => {
  //   // If value changes, update mode accordingly
  //   setIsTextMode(value && !isValidDateValue(value));
  // }, [value]);

  return (
    <FieldWrapper field={field}>
      <div className="flex gap-2 items-center">
        <Input
          id={field.id}
          type={isTextMode ? "text" : "date"}
          placeholder={isTextMode ? "" : field.placeholder}
          disabled={disabled || isDeclined}
          required={field.required && !isDeclined}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          className="user-invalid:border-destructive"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsTextMode(!isTextMode)}
          disabled={disabled || isDeclined}
          title={isTextMode ? "Switch to date picker" : "Switch to text input"}
        >
          <ArrowLeftRight className="inline w-4 h-4" />
          {isTextMode ? "Date picker" : "Text"}
        </Button>
      </div>
      {field.required && (
        <DeclineCheckbox
          field={field}
          isDeclined={isDeclined}
          onDeclineChange={(declined) => {
            setIsDeclined(declined);
            onChange(declined ? null : "");
          }}
        />
      )}
    </FieldWrapper>
  );
}

function TimeField({ field, value, onChange, disabled }: BaseFieldProps) {
  const [isDeclined, setIsDeclined] = React.useState(false);

  // Determine if value is a valid time for <input type="time">
  const isValidTimeValue = (val: FieldValue) => {
    if (typeof val !== "string") return false;
    // Accepts HH:MM or HH:MM:SS format
    return /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/.test(val);
  };

  const initialTextMode = value && !isValidTimeValue(value);
  const [isTextMode, setIsTextMode] = React.useState(initialTextMode);

  return (
    <FieldWrapper field={field}>
      <div className="flex gap-2 items-center">
        <Input
          id={field.id}
          type={isTextMode ? "text" : "time"}
          placeholder={isTextMode ? "" : field.placeholder}
          disabled={disabled || isDeclined}
          required={field.required && !isDeclined}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          className="user-invalid:border-destructive"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsTextMode(!isTextMode)}
          disabled={disabled || isDeclined}
          title={isTextMode ? "Switch to time picker" : "Switch to text input"}
        >
          <ArrowLeftRight className="inline w-4 h-4" />
          {isTextMode ? "Time picker" : "Text"}
        </Button>
      </div>
      {field.required && (
        <DeclineCheckbox
          field={field}
          isDeclined={isDeclined}
          onDeclineChange={(declined) => {
            setIsDeclined(declined);
            onChange(declined ? null : "");
          }}
        />
      )}
    </FieldWrapper>
  );
}

function DateTimeField({ field, value, onChange, disabled }: BaseFieldProps) {
  const [isDeclined, setIsDeclined] = React.useState(false);

  const isValidDateTimeValue = (val: FieldValue) => {
    if (typeof val !== "string") return false;
    // Accepts YYYY-MM-DDTHH:MM or YYYY-MM-DDTHH:MM:SS format
    return /^\d{4}-\d{2}-\d{2}T([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/.test(val);
  };

  const initialTextMode = value && !isValidDateTimeValue(value);
  const [isTextMode, setIsTextMode] = React.useState(initialTextMode);

  return (
    <FieldWrapper field={field}>
      <div className="flex gap-2 items-center">
        <Input
          id={field.id}
          type={isTextMode ? "text" : "datetime-local"}
          placeholder={isTextMode ? "" : field.placeholder}
          disabled={disabled || isDeclined}
          required={field.required && !isDeclined}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          className="user-invalid:border-destructive"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsTextMode(!isTextMode)}
          disabled={disabled || isDeclined}
          title={
            isTextMode ? "Switch to date-time picker" : "Switch to text input"
          }
        >
          <ArrowLeftRight className="inline w-4 h-4" />
          {isTextMode ? "Date-Time picker" : "Text"}
        </Button>
      </div>

      {field.required && (
        <DeclineCheckbox
          field={field}
          isDeclined={isDeclined}
          onDeclineChange={(declined) => {
            setIsDeclined(declined);
            onChange(declined ? null : "");
          }}
        />
      )}
    </FieldWrapper>
  );
}

function NoteField({ field }: { field: FormField }) {
  return (
    <div className="bg-primary/5 p-3 rounded-sm shadow-md border-primary border">
      <div className="flex items-center space-x-2 mb-2">
        <InfoIcon className="h-4 w-4 text-primary" />
        <span className="font-medium text-primary">{field.label}</span>
      </div>
      <p className="text-sm text-primary">{field.note}</p>
    </div>
  );
}

// Helper components
function FieldWrapper({
  field,
  children,
}: {
  field: FormField;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={field.id}>
        {field.label}
        {field.required && <span className="text-destructive ml-1">*</span>}
      </Label>
      {children}
      {field.description && (
        <p className="text-muted-foreground text-sm">{field.description}</p>
      )}
      {field.note && <FormFieldNote note={field.note} />}
    </div>
  );
}

function DeclineCheckbox({
  field,
  isDeclined,
  onDeclineChange,
}: {
  field: FormField;
  isDeclined: boolean;
  onDeclineChange: (declined: boolean) => void;
}) {
  return (
    <div className="flex items-center space-x-2">
      <Checkbox
        id={`${field.id}-declined`}
        checked={isDeclined}
        onCheckedChange={onDeclineChange}
        className="border-destructive focus-visible:ring-destructive data-[state=checked]:bg-destructive data-[state=checked]:text-destructive-foreground"
      />
      <Label
        htmlFor={`${field.id}-declined`}
        className="text-sm text-muted-foreground"
      >
        Unable to collect this information
      </Label>
    </div>
  );
}
